import { StyleSheet, Text, View } from 'react-native';

import { Fonts, Radius, Spacing } from '@/constants/theme';
import { useTheme } from '@/hooks/use-theme';
import type { CityGuide } from '@/lib/i18n/city-content';
import type { CityClimateCopy } from '@/lib/i18n/climate-content';

import { CityClimateTable } from './city-climate';

// The city page's long-form SEO guide (things to do, events and when to
// visit, getting there), the weather-by-month table and the FAQ, rendered
// below the listings so the top of the page stays hero + cards. The FAQ is
// the visible counterpart of the page's FAQPage JSON-LD — Google requires the
// two to match, so the page passes the same merged list (hand-written +
// climate entries) to both.
export function CityGuideBlock({
  guide,
  climate,
  faqHeading,
}: {
  guide: CityGuide;
  climate?: CityClimateCopy;
  faqHeading: string;
}) {
  const theme = useTheme();

  return (
    <View style={[styles.root, { borderTopColor: theme.border }]}>
      {guide.sections.map((section) => (
        <View key={section.heading} style={styles.section}>
          <Text
            role="heading"
            aria-level={2}
            style={[styles.heading, { color: theme.text, fontFamily: Fonts?.serif }]}>
            {section.heading}
          </Text>
          {section.paragraphs.map((paragraph, index) => (
            <Text key={index} style={[styles.paragraph, { color: theme.text }]}>
              {paragraph}
            </Text>
          ))}
        </View>
      ))}

      {climate ? <CityClimateTable copy={climate} /> : null}

      {guide.faq.length > 0 ? (
        <View style={styles.section}>
          <Text
            role="heading"
            aria-level={2}
            style={[styles.heading, { color: theme.text, fontFamily: Fonts?.serif }]}>
            {faqHeading}
          </Text>
          {guide.faq.map((entry) => (
            <View
              key={entry.q}
              style={[styles.faqItem, { backgroundColor: theme.card, borderColor: theme.border }]}>
              <Text
                role="heading"
                aria-level={3}
                style={[styles.question, { color: theme.text }]}>
                {entry.q}
              </Text>
              <Text style={[styles.answer, { color: theme.textSecondary }]}>{entry.a}</Text>
            </View>
          ))}
        </View>
      ) : null}
    </View>
  );
}

const styles = StyleSheet.create({
  root: {
    marginTop: Spacing.four,
    paddingTop: Spacing.five,
    borderTopWidth: 1,
    gap: Spacing.five,
  },
  section: {
    gap: Spacing.three,
  },
  heading: {
    fontSize: 22,
    lineHeight: 28,
    fontWeight: '700',
  },
  paragraph: {
    fontSize: 16,
    lineHeight: 26,
  },
  faqItem: {
    gap: Spacing.two,
    padding: Spacing.three,
    borderRadius: Radius.lg,
    borderWidth: 1,
  },
  question: {
    fontSize: 16,
    lineHeight: 22,
    fontWeight: '700',
  },
  answer: {
    fontSize: 15,
    lineHeight: 24,
  },
});
